import Twt3dConfig from '~/tenants/twt/performable/matterport/TwtMatterportConfig';
import { TwtSimpleJob } from '~/tenants/twt/performable/TwtSimpleJob';
import { DisplayData, TenantJobLine, TenantOrderLine } from '~/lib/model';
import Big from 'big.js';
import { TwtPropertyType } from '~/tenants/twt/model/TwtOrder';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { DeliverableType } from '~common/model/Deliverable';
import { TwtOrderType } from '~/tenants/twt/model/enums';
import { TWT_COMMERCIAL_3D_MINIMUM, twt_version, TwtVersion } from '~/tenants/twt/data';
import { OrderCustomTag } from '~/lib/schema/order';
import { datetime } from '~/lib/datettime';

export default class TwtMatterportJob extends TwtSimpleJob<typeof Twt3dConfig> {
  protected get config() {
    return {
      onsiteMinimum: 30,
      onsitePer1000: 10,
      expenseMinimum: 55,
      expensePer1000: 30,
    };
  }

  get performable() {
    return Twt3dConfig;
  }

  get deliverable() {
    return this.metadata.nucleus ? DeliverableType.NUCLEUS : DeliverableType.MATTERPORT;
  }

  get short() {
    return '3D';
  }

  defaultValue(): ZodVersionedMetadata<(typeof Twt3dConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      floors: 1,
    };
  }

  get review() {
    return Boolean(this.order.parent?.matterport_transfer ?? this.order.buyer.metadata.matterport_transfer)
      ? 'transferred'
      : false;
  }

  onsite(): number {
    if(this.order.metadata.transfer){
      return 0;
    }

    if (this.metadata.nucleus) {
      return 45;
    }

    return this.onsiteWithProviderBands('scan_onsite');
  }

  get tags(): OrderCustomTag[] {
    const tags = super.tags;

    if (this.review) {
      tags.push({
        name: 'Transfer 3D Scan',
        color: 'teal',
      });
    }

    return tags;
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Floors',
      value: this.metadata.floors.toString(),
      provider: true,
      schedule: true,
    });

    if (this.metadata.detached && this.metadata.detached.length > 0) {
      info.push({
        name: 'Detached Structures',
        value: this.metadata.detached.join(','),
        provider: true,
        acknowledge: true,
        schedule: true,
        customer: true,
      });
    }

    if (this.metadata.branded_url) {
      info.push({
        name: 'Branded URL',
        value: this.metadata.branded_url,
        provider: true,
      });
    }

    if (this.metadata.unbranded_url) {
      info.push({
        name: 'Unbranded URL',
        value: this.metadata.unbranded_url,
        provider: true,
      });
    }

    return info;
  }

  get eligible(): boolean {
    return this.order.metadata.subtype !== TwtPropertyType.LAND;
  }

  get configurable() {
    return true;
  }

  protected revenueMinimum() {
    return this.orderHasPhotos && this.orderHasFloorplan ? 120 : 125;
  }

  protected revenuePer() {
    return this.orderHasPhotos && this.orderHasFloorplan ? twt_version(this.order.created, TwtVersion.V18) ? 65 : 50 : 80;
  }

  protected expensePer(thousands: number) {
    return thousands < 3 ? 35 : 30;
  }

  expenseLines(): TenantJobLine[] {
    let amount = this.simpleExpense();

    // From V29 (Paul, 10/19/26) the photographer is paid for a commercial
    // scan exactly what a residential one of the same size pays (above);
    // before it, per square foot.
    if (this.order.metadata.type === TwtOrderType.COMMERCIAL && !twt_version(this.order.created, TwtVersion.V29)) {
      amount = new Big(twt_version(this.order.created, TwtVersion.V9) ? '0.07' : '0.05').times(this.sqft);
    }

    if (!twt_version(this.order.created, TwtVersion.V4)) {
      const mapping: Record<string, string> = {
        '90.00': '80.00',
        '55.00': '50.00',
      };

      if (mapping[amount.toFixed(2)]) {
        amount = new Big(mapping[amount.toFixed(2)]);
      }
    }

    return [
      {
        amount,
        description: '3D Scan',
        id: '3d',
      },
    ];
  }

  revenueLines(): TenantOrderLine[] {
    const lines = super.revenueLines();

    let amount = this.simpleRevenue().amount;

    if (this.order.metadata.type === TwtOrderType.COMMERCIAL) {
      amount = new Big(twt_version(this.order.created, TwtVersion.V9) ? '0.15' : '0.10').times(this.sqft);

      // A $350 minimum from V29 (Paul, 10/19/26): 0.15 x sqft had no floor.
      // The photographer's pay is the residential scan's (expenseLines).
      if (twt_version(this.order.created, TwtVersion.V29) && amount.lt(TWT_COMMERCIAL_3D_MINIMUM)) {
        amount = new Big(TWT_COMMERCIAL_3D_MINIMUM);
      }
    }

    if (!twt_version(this.order.created, TwtVersion.V4)) {
      const mapping: Record<string, string> = {
        '205.00': '170.00',
        '125.00': '120.00',
      };

      if (mapping[amount.toFixed(2)]) {
        amount = new Big(mapping[amount.toFixed(2)]);
      }
    }

    lines.push({
      amount,
      description: '3D Scan',
      id: '3d',
      discountable: true,
      taxable: true,
    });

    const discountDollars = new Big(this.buyer('matterport_discount_dollars') ?? '0');
    const discountPct = new Big(this.buyer('matterport_discount_percentage') ?? 0).div(100);
    const discountDate = datetime(this.buyer('matterport_discount_date') ?? this.order.created, this.order.timezone);

    if (datetime(this.order.created, this.order.timezone) >= discountDate) {
      if (discountDollars.gt(0)) {
        lines.push({
          amount: discountDollars.times(-1),
          description: 'Matterport Discount',
          id: 'matterport_discount',
          taxable: true,
          discount: true,
        });
      } else if (!discountPct.eq(0)) {
        lines.push({
          amount: lines
            .reduce((acc, line) => acc.plus(line.amount), new Big(0))
            .times(discountPct)
            .times(-1),
          description: 'Matterport Discount',
          id: 'matterport_discount',
          taxable: true,
          discount: true,
        });
      }
    }

    return lines;
  }
}
